/**
 * A prévia da lista de sonhos no painel: o `bucket_list_preview.dart`.
 *
 * É a fatia de `dashboard_screen.dart:529`, e é a única prévia que não mora
 * dentro de uma `Secao`: lá ela desenha o próprio cartão de vidro, com o
 * cabeçalho por dentro e o anel de progresso no lugar da pastilha do ícone.
 *
 * O que o navegador obrigou a mudar:
 *
 * - **Sem emoji.** Lá o anel tem um `'🌟'` no meio e cada sonho da lista mostra
 *   o `category.emoji`. Aqui a casa desenha com a mesma biblioteca de traços do
 *   resto do site — emoji em texto de interface é regra da casa, não gosto.
 * - **O anel é um `<svg>`.** O `CircularProgressIndicator` de lá é um widget;
 *   aqui são dois círculos, o de trás na trilha e o da frente cortado no
 *   tanto que já foi. Deitado 90 graus para a volta começar no topo, como lá.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  COLECOES,
  categoriaDoDesejo,
  traduzir,
  usarColecao,
  type DocDesejo,
} from '@nosso-sempre/shared';
import { Icone } from './Icone';
import './previas.css';

/** O `.take(3)` de lá: os três primeiros sonhos ainda por realizar. */
const QUANTOS = 3;

/** O anel de 40 com traço 3: raio que cabe sem cortar a ponta do traço. */
const RAIO = 18.5;
const VOLTA = 2 * Math.PI * RAIO;

export function PreviaDosDesejos() {
  const itens = usarColecao<DocDesejo>(COLECOES.listaDesejos);

  const { feitos, sonhando } = useMemo(
    () => ({
      feitos: itens.filter((i) => i.dados.status === 'completed').length,
      sonhando: itens.filter((i) => (i.dados.status ?? 'wish') === 'wish').slice(0, QUANTOS),
    }),
    [itens],
  );

  const total = itens.length;

  if (total === 0) {
    return (
      <Link className="previa-sonhos" to="/desejos">
        <span className="previa-sonhos-topo">
          <span className="previa-sonhos-estrela" aria-hidden="true">
            <Icone nome="estrela" tamanho={18} />
          </span>
          <span className="previa-sonhos-conta previa-sonhos-dizeres">
            {traduzir('A lista de sonhos está vazia. Comecem pelo primeiro!')}
          </span>
          <span className="previa-sonhos-seta" aria-hidden="true">
            <Icone nome="mais-circulo" tamanho={18} />
          </span>
        </span>
      </Link>
    );
  }

  const feito = VOLTA * (feitos / total);

  return (
    <Link
      className="previa-sonhos"
      to="/desejos"
      aria-label={traduzir('Lista de sonhos: {feitos} de {total} realizados. Toque para abrir.', {
        feitos,
        total,
      })}
    >
      <span className="previa-sonhos-topo">
        <span className="previa-sonhos-anel" aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 40 40">
            <circle
              cx="20"
              cy="20"
              r={RAIO}
              fill="none"
              stroke="color-mix(in srgb, var(--petala) 10%, transparent)"
              strokeWidth="3"
            />
            <circle
              cx="20"
              cy="20"
              r={RAIO}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${feito} ${VOLTA}`}
            />
          </svg>
          <Icone nome="desejo" tamanho={16} />
        </span>

        <span className="previa-sonhos-dizeres">
          <span className="previa-sonhos-titulo">{traduzir('Nossa lista de sonhos')}</span>
          <span className="previa-sonhos-conta">
            {traduzir('{feitos} de {total} sonhos realizados', { feitos, total })}
          </span>
        </span>

        <span className="previa-sonhos-seta" aria-hidden="true">
          <Icone nome="avancar" tamanho={18} />
        </span>
      </span>

      {sonhando.length > 0 ? (
        <ul className="previa-sonhos-lista">
          {sonhando.map((i) => (
            <li className="previa-sonhos-item" key={i.id}>
              <Icone nome={categoriaDoDesejo(i.dados.category).icone} tamanho={12} />
              <span>{i.dados.title}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </Link>
  );
}
